// Medidor da franquia do Free: "X de 250 interações do mês (enviadas + recebidas)".
//
// Vive onde o consumo da conta já é mostrado (Dashboard → card "Plano", e
// Cobrança → acima dos planos). Some inteiro no Pro e na cortesia: lá não
// existe franquia, e um medidor cheio só assustaria quem já paga.
import { Link } from 'react-router-dom';

import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useFreeQuota, type FreeQuotaState } from '@/lib/free-quota';

/** Cor da barra: calma até 80%, âmbar no aviso, vermelha quando pausou. */
function tone(q: FreeQuotaState) {
  if (q.blocked) return 'bg-destructive';
  if (q.warning) return 'bg-amber-500';
  return 'bg-primary';
}

export function FreeQuotaGauge({ className, showUpgrade = true }: {
  className?: string;
  /** Mostra o link de upgrade quando a franquia está apertada (na Cobrança já há os planos). */
  showUpgrade?: boolean;
}) {
  const { locale, t } = useI18n();
  const q = useFreeQuota();
  if (!q.metered) return null;

  const n = (v: number) => v.toLocaleString(locale);
  const resetDate = q.resetsAt.toLocaleDateString(locale, { day: '2-digit', month: 'short' });

  return (
    <div className={cn('rounded-lg border bg-muted/30 p-4', className)}>
      <p className="text-xs text-muted-foreground">{t('freeQuota.title')}</p>
      <p className="mt-1 text-sm font-semibold">
        {t('freeQuota.usage', { used: n(q.used), included: n(q.included) })}
      </p>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={q.pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('freeQuota.title')}
      >
        <div className={cn('h-full rounded-full transition-all', tone(q))} style={{ width: `${q.pct}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t('freeQuota.resets', { date: resetDate })}</p>
        {showUpgrade && (q.warning || q.blocked) && (
          <Link to="/billing" className="text-xs font-semibold text-primary hover:underline">
            {t('freeQuota.upgrade')}
          </Link>
        )}
      </div>
    </div>
  );
}
